import { useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getAuthCookieInfo } from 'utils/functions/authentication';
import { fetchUploadContentSubmission } from 'modules/labs/common/actions';
import { entityTypeEnum } from 'modules/labs/common/enums';
import {
  orderStatusEnum,
  orderStatusToEnableLabAssignMenu,
  orderStatusToEnableEditInvoice,
  hideButtonBasedOnState,
  orderStatusToShowInProgressBar,
} from 'modules/labs/orders/enums';
import {
  fetchOrderDetails,
  fetchAssignOrderToEntity,
  fetchOrderServicesSuggestions,
  resetOrderServicesSuggestions,
  fetchOrderServicesSubmission,
  fetchOrderStatusChangeSubmission,
  fetchOrderNoteChangeSubmission,
  fetchScheduleOrderSubmission,
  fetchScheduleOrderReasons,
  fetchCancelOrderReasons,
} from 'modules/labs/orders/actions';
import ordersStrings from 'modules/labs/orders/strings';
import OrderDetailsContainer from 'modules/shared/orders/containers/OrderDetailsContainer';
import { ThemeContext } from 'styled-components';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const OrderDetailsView = props => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const labsList = useSelector(state => state.common.labsList);
  const chemistsList = useSelector(state => state.common.chemistsList);
  const usersList = useSelector(state => state.common.usersList);
  const uploadContentSubmission = useSelector(state => state.common.uploadContentSubmission);
  const orderDetails = useSelector(state => state.labsOrders.orderDetails);
  const assignOrderToEntityDetails = useSelector(state => state.labsOrders.assignOrderToEntity);
  const orderServicesSuggestions = useSelector(state => state.labsOrders.orderServicesSuggestions);
  const orderServicesSubmission = useSelector(state => state.labsOrders.orderServicesSubmission);
  const orderNoteChangeSubmission = useSelector(
    state => state.labsOrders.orderNoteChangeSubmission,
  );
  const loginInfo = useSelector(state => state.accountManagement.loginInfo);
  const scheduleOrderReasons = useSelector(state => state.labsOrders.scheduleOrderReasons);
  const cancelOrderReasons = useSelector(state => state.labsOrders.cancelOrderReasons);
  const dispatch = useDispatch();
  const { id: orderId } = useParams();

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [labAssignDropdownMenuSelectedObj, setLabAssignDropdownMenuSelectedObj] = useState({
    key: '',
    name: '',
  });
  const [actionPopupStatus, setActionPopupStatus] = useState(0);
  const [mappedOrderStatus, setMappedOrderStatus] = useState([]);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    if (orderDetails.details?.selectedLabObj)
      setLabAssignDropdownMenuSelectedObj(orderDetails.details?.selectedLabObj);
  }, [orderDetails]);

  useEffect(() => {
    const orderData = orderDetails?.details;
    if (!orderData?.orderStateTypeId) return;
    const orderStatusId = orderData?.orderStateTypeId;
    const orderLogs = orderData?.orderStates;
    const orderLogsStatusArr = orderLogs
      ?.sort((a, b) => {
        if (a.timeStamp > b.timeStamp) return 1;
        if (a.timeStamp < b.timeStamp) return -1;
        return 0;
      })
      .map(log => log.orderStateTypeId);
    const orderStatusEnumArr = Object.keys(orderStatusEnum);

    let orderStatusToShowInProgressBarArr = [...orderStatusToShowInProgressBar];
    if (orderLogsStatusArr?.includes(orderStatusEnum.Scheduled))
      orderStatusToShowInProgressBarArr.splice(1, 0, orderStatusEnum.Scheduled);
    if (orderStatusId === orderStatusEnum.Cancelled) {
      orderStatusToShowInProgressBarArr = [...orderLogsStatusArr];
    }

    const tempMappedOrderStatus = orderStatusToShowInProgressBarArr.map(orderStatus => {
      const orderStatusName = orderStatusEnum.getKeyByValue(orderStatus);
      let orderStatusColor = '';
      if (
        orderStatusEnumArr.indexOf(orderStatusEnum.getKeyByValue(orderStatus)) <=
        orderStatusEnumArr.indexOf(orderStatusEnum.getKeyByValue(orderStatusId))
      )
        orderStatusColor = colors.greenMain;
      if (orderStatus === orderStatusEnum.Scheduled) orderStatusColor = colors.blueSkyDark;
      if (orderStatus === orderStatusEnum.Cancelled) orderStatusColor = colors.redMain;

      return {
        orderStatusName,
        orderStatusColor,
      };
    });

    setMappedOrderStatus([...tempMappedOrderStatus]);
  }, [orderDetails, colors]);

  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################

  const serviceObjectMapper = service => ({
    key: service?.ServiceKey,
    name: service?.ServiceTranslators[0].Name,
    price: service?.ServicePrice,
    isSearchable: service?.IsSearchable,
  });

  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const reloadOrderDetailsPage = () => {
    const body = {
      orderId,
    };
    dispatch(fetchOrderDetails(body));
  };

  const updateOrderItemsCallback = () => {
    reloadOrderDetailsPage();
  };

  const getServicesSuggestionsData = searchQuery => {
    const body = {
      data: {
        query: searchQuery,
      },
      headers: {
        Language: 'en-US',
      },
    };

    dispatch(fetchOrderServicesSuggestions(body));
  };

  const resetServicesSuggestionsData = () => {
    dispatch(resetOrderServicesSuggestions());
  };

  const submitOrderInvoiceDispensedItems = orderServices => {
    const body = {
      orderId,
      data: {
        orderServices,
      },
      callbackAction: updateOrderItemsCallback,
    };

    dispatch(fetchOrderServicesSubmission(body));
  };

  const actionBtnClick = actionStatus => {
    setActionPopupStatus(actionStatus);
  };

  const actionPopupDismissBtn = () => {
    setActionPopupStatus(0);
  };

  const actionPopupConfirmBtn = actionPopupConfirmBtnCallbackFn => {
    const body = {
      data: {
        entityId: loginInfo.details.data.key,
        EntityTypeId: entityTypeEnum.LabsUser,
        orderKey: orderId,
        note: '',
        orderStateTypeId: actionPopupStatus,
      },
      callbackAction: actionPopupConfirmBtnCallbackFn,
    };

    dispatch(fetchOrderStatusChangeSubmission(body));
  };

  const actionPopupConfirmBtnCallback = shouldReloadOrderDetails => {
    setActionPopupStatus(0);

    if (shouldReloadOrderDetails) reloadOrderDetailsPage();
  };

  const updateOrderNote = orderNote => {
    const body = {
      orderId,
      data: [
        {
          value: orderNote,
          path: '/internalNote',
          op: 'replace',
        },
      ],
    };

    dispatch(fetchOrderNoteChangeSubmission(body));
  };

  const uploadOrderResultsCallback = () => {
    reloadOrderDetailsPage();
  };

  const scheduleOrderPopupConfirmBtn = (data, scheduleOrderPopupConfirmBtnCallbackFn) => {
    const body = {
      orderId,
      data,
      callbackAction: scheduleOrderPopupConfirmBtnCallbackFn,
    };

    dispatch(fetchScheduleOrderSubmission(body));
  };

  const cancelOrderPopupConfirmBtn = (data, cancelOrderPopupConfirmBtnCallbackFn) => {
    const body = {
      orderId,
      data,
      callbackAction: cancelOrderPopupConfirmBtnCallbackFn,
    };
    dispatch(fetchOrderStatusChangeSubmission(body));
  };

  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleLabAssignDropdownMenuChange = selectedObj => {
    setLabAssignDropdownMenuSelectedObj(selectedObj);

    const { userKey } = getAuthCookieInfo();
    const assignOrderData = {
      data: {
        OrderKey: orderId,
        LocationKey: selectedObj.key,
        ActionTakerKey: userKey,
      },
    };
    dispatch(fetchAssignOrderToEntity(assignOrderData));
  };

  const handleOrderRefreshBtnClick = () => {
    reloadOrderDetailsPage();
  };

  const handleOrderSendResultsBtnClick = orderResultsArr => {
    const orderResults = orderResultsArr.map(result => result.compressedFileData);

    if (orderResults.length === 0) return;

    const body = {
      orderId,
      files: orderResults,
      callbackAction: uploadOrderResultsCallback,
    };

    dispatch(fetchUploadContentSubmission(body));
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <OrderDetailsContainer
      stringsData={ordersStrings}
      orderDetails={orderDetails}
      labAssignDropdownMenuItemsList={labsList}
      labAssignDropdownMenuSelectedObj={labAssignDropdownMenuSelectedObj}
      chemistsList={chemistsList}
      usersList={usersList}
      assignOrderToEntityDetails={assignOrderToEntityDetails}
      orderServicesSuggestions={orderServicesSuggestions}
      orderServicesSubmission={orderServicesSubmission}
      uploadContentSubmission={uploadContentSubmission}
      orderNoteChangeSubmission={orderNoteChangeSubmission}
      loginInfo={loginInfo}
      actionPopupStatus={actionPopupStatus}
      handleLabAssignDropdownMenuChange={handleLabAssignDropdownMenuChange}
      handleOrderRefreshBtnClick={handleOrderRefreshBtnClick}
      handleOrderSendResultsBtnClick={handleOrderSendResultsBtnClick}
      getServicesSuggestionsData={getServicesSuggestionsData}
      resetServicesSuggestionsData={resetServicesSuggestionsData}
      submitOrderInvoiceDispensedItems={submitOrderInvoiceDispensedItems}
      actionBtnClick={actionBtnClick}
      actionPopupDismissBtn={actionPopupDismissBtn}
      actionPopupConfirmBtn={actionPopupConfirmBtn}
      actionPopupConfirmBtnCallback={actionPopupConfirmBtnCallback}
      updateOrderNote={updateOrderNote}
      scheduleOrderPopupConfirmBtn={scheduleOrderPopupConfirmBtn}
      cancelOrderPopupConfirmBtn={cancelOrderPopupConfirmBtn}
      fetchScheduleOrderReasons={fetchScheduleOrderReasons}
      fetchCancelOrderReasons={fetchCancelOrderReasons}
      scheduleOrderReasons={scheduleOrderReasons}
      cancelOrderReasons={cancelOrderReasons}
      orderStatusEnum={orderStatusEnum}
      orderStatusToEnableAssignMenu={orderStatusToEnableLabAssignMenu}
      orderStatusToEnableEditInvoice={orderStatusToEnableEditInvoice}
      objectMapper={serviceObjectMapper}
      hideButtonBasedOnState={hideButtonBasedOnState}
      mappedOrderStatus={mappedOrderStatus}
      {...props}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

export default OrderDetailsView;
